import { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Plus, Trash2, Zap, RefreshCw, Target } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CompetitiveIntel() {
  const [competitors, setCompetitors] = useState([]);
  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [report, setReport] = useState(null);
  const [latestReady, setLatestReady] = useState(false);

  const loadCompetitors = async () => {
    try {
      const { data } = await axios.get(`${API}/api/intel/competitors`);
      setCompetitors(data.competitors || []);
    } catch { }
  };

  const loadLatest = async () => {
    try {
      const { data } = await axios.get(`${API}/api/intel/latest`);
      if (data.report) setReport(data.report);
    } catch { }
  };

  useEffect(() => { loadCompetitors(); loadLatest(); }, []);

  const addCompetitor = async () => {
    if (!newName || !newUrl) return;
    await axios.post(`${API}/api/intel/competitors`, { name: newName, url: newUrl });
    setNewName(''); setNewUrl('');
    loadCompetitors();
  };

  const removeCompetitor = async (id) => {
    await axios.delete(`${API}/api/intel/competitors/${id}`);
    loadCompetitors();
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const { data } = await axios.post(`${API}/api/intel/analyze`, {
        your_topics: ['AI', 'Cybersecurity', 'Leadership', 'Digital Transformation'],
        your_industry: 'AI & Technology',
      });
      setReport(data.report);
    } catch (e) { console.error(e); }
    finally { setAnalyzing(false); }
  };

  const priorityColor = { high: '#eb5757', medium: '#f0c27f', low: '#00d4b8' };

  return (
    <div className="h-full overflow-y-auto" style={{ background: 'var(--llmc-bg)' }}>
      <div className="max-w-4xl mx-auto p-6 flex flex-col gap-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #ff5370, #b71c1c)' }}>
            <Shield size={20} className="text-white" />
          </div>
          <div>
            <h1 className="font-black text-white text-lg" style={{ letterSpacing: '-0.02em' }}>Competitive Intelligence</h1>
            <p className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>Map competitor strategies · Find content gaps · Get AI strategic recommendations</p>
          </div>
          <button onClick={runAnalysis} disabled={analyzing} data-testid="run-analysis-btn"
            className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-white"
            style={{ background: 'linear-gradient(135deg, #ff5370, #b71c1c)' }}>
            {analyzing
              ? <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#fff', borderTopColor: 'transparent' }} />
              : <Zap size={14} />}
            {analyzing ? 'Analyzing...' : 'Run Analysis'}
          </button>
        </div>

        {/* Add Competitor */}
        <div className="llmc-card p-4">
          <h2 className="text-sm font-bold text-white mb-3">Track Competitors</h2>
          <div className="flex gap-2">
            <input value={newName} onChange={e => setNewName(e.target.value)}
              placeholder="Competitor name" className="flex-1 llmc-input px-3 py-2 text-sm"
              data-testid="competitor-name-input" />
            <input value={newUrl} onChange={e => setNewUrl(e.target.value)}
              placeholder="https://..." className="flex-1 llmc-input px-3 py-2 text-sm"
              data-testid="competitor-url-input" />
            <button onClick={addCompetitor} data-testid="add-competitor-btn"
              className="w-9 h-9 rounded-xl flex items-center justify-center llmc-btn-primary">
              <Plus size={16} />
            </button>
          </div>

          {competitors.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {competitors.map(c => (
                <div key={c.id} className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
                  style={{ background: 'rgba(255,83,112,0.1)', border: '1px solid rgba(255,83,112,0.3)' }}>
                  <Target size={10} style={{ color: '#ff5370' }} />
                  <span style={{ color: 'var(--llmc-text)' }}>{c.name}</span>
                  <button onClick={() => removeCompetitor(c.id)} className="hover:opacity-80">
                    <Trash2 size={10} style={{ color: 'rgba(235,87,87,0.7)' }} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {competitors.length === 0 && (
            <p className="text-xs mt-3" style={{ color: 'rgba(255,255,255,0.3)' }}>
              No competitors tracked yet. Add some or run analysis with defaults (TechCrunch, Wired, HackerNews).
            </p>
          )}
        </div>

        {/* Report */}
        {report && (
          <div className="fade-in llmc-card p-5 flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-bold text-white">Intelligence Report</h2>
              <span className="text-[10px] px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(61,255,160,0.1)', color: '#3dffa0', border: '1px solid rgba(61,255,160,0.3)' }}>
                {new Date(report.created_at).toLocaleDateString()}
              </span>
            </div>

            {report.battlefield_summary && (
              <div className="p-4 rounded-xl" style={{ background: 'rgba(255,83,112,0.08)', border: '1px solid rgba(255,83,112,0.2)' }}>
                <p className="text-sm leading-relaxed" style={{ color: '#ffb3be' }}>{report.battlefield_summary}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {report.content_gaps?.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold mb-2" style={{ color: '#3dffa0' }}>Content Gaps</h3>
                  <ul className="flex flex-col gap-1.5">
                    {report.content_gaps.map((g, i) => (
                      <li key={i} className="text-xs flex items-start gap-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>
                        <span style={{ color: '#3dffa0', flexShrink: 0 }}>→</span>{g}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {report.strategic_opportunities?.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold mb-2" style={{ color: '#7c5efa' }}>Strategic Opportunities</h3>
                  <ul className="flex flex-col gap-1.5">
                    {report.strategic_opportunities.map((o, i) => (
                      <li key={i} className="text-xs flex items-start gap-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>
                        <span style={{ color: '#7c5efa', flexShrink: 0 }}>→</span>{o}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {report.recommended_content_moves?.length > 0 && (
              <div>
                <h3 className="text-xs font-bold mb-2" style={{ color: '#f0c27f' }}>Recommended Content Moves</h3>
                <div className="flex flex-col gap-2">
                  {report.recommended_content_moves.map((m, i) => (
                    <div key={i} className="p-3 rounded-xl flex gap-3"
                      style={{ background: 'rgba(240,194,127,0.06)', border: '1px solid rgba(240,194,127,0.15)' }}>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded self-start"
                        style={{ background: `${priorityColor[m.priority] || '#888'}22`, color: priorityColor[m.priority] || '#888', border: `1px solid ${priorityColor[m.priority] || '#888'}44` }}>
                        {m.priority?.toUpperCase()}
                      </span>
                      <div>
                        <div className="text-xs font-semibold text-white">{m.title}</div>
                        <div className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>{m.rationale}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
